import React, { useEffect, useState } from 'react';
import './FeedbackManagement.css';

interface FeedbackItem {
  id: string;
  eventId: string;
  userId: string;
  eventName: string;
  eventDate: string;
  overallRating: number;
  platformRating: number;
  organizerRating: number;
  organizerName: string;
  testimonial?: {
    text: string;
    allowPublicUse: boolean;
    participantName: string;
  };
  publicText?: string;
  approvedForPublic?: boolean;
  createdAt: string;
}

interface MealExperience {
  id: string;
  eventId: string;
  userId: string;
  mealType: string;
  mealName: string;
  description: string;
  estimatedCost: number;
  eventName: string;
  eventDate: string;
  eventLocation: string;
  approvedForPublic?: boolean;
  submittedAt: string;
}

export const FeedbackManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [mealExperiences, setMealExperiences] = useState<MealExperience[]>([]);
  const [error, setError] = useState('');
  const [view, setView] = useState<'feedback' | 'meals'>('feedback');
  const [editingFeedback, setEditingFeedback] = useState<string | null>(null);
  const [editingMeal, setEditingMeal] = useState<string | null>(null);
  const [editedText, setEditedText] = useState('');
  const [editedMealName, setEditedMealName] = useState('');
  const [editedMealDesc, setEditedMealDesc] = useState('');

  useEffect(() => {
    fetchData();
  }, [view]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      if (view === 'feedback') {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/feedback/admin/all`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error('Kunne ikke hente tilbakemeldinger');
        const data = await response.json();
        setFeedback(data.feedback || []);
      } else {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/feedback/admin/meal-experiences`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error('Kunne ikke hente måltidserfaringer');
        const data = await response.json();
        setMealExperiences(data.experiences || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateFeedback = async (feedbackId: string, publicText: string, approvedForPublic: boolean) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/feedback/admin/${feedbackId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ publicText, approvedForPublic }),
        }
      );

      if (!response.ok) throw new Error('Kunne ikke oppdatere tilbakemelding');

      // Update local state
      setFeedback((prev) =>
        prev.map((f) =>
          f.id === feedbackId ? { ...f, publicText, approvedForPublic } : f
        )
      );
      setEditingFeedback(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateMealExperience = async (
    experienceId: string,
    mealName: string,
    description: string,
    approvedForPublic: boolean
  ) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/feedback/admin/meal-experiences/${experienceId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ mealName, description, approvedForPublic }),
        }
      );

      if (!response.ok) throw new Error('Kunne ikke oppdatere måltidserfaring');

      // Update local state
      setMealExperiences((prev) =>
        prev.map((m) =>
          m.id === experienceId
            ? { ...m, mealName, description, approvedForPublic }
            : m
        )
      );
      setEditingMeal(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="stars-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star} className={star <= rating ? 'star filled' : 'star'}>
            ★
          </span>
        ))}
      </div>
    );
  };

  const getMealTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      appetizer: 'Forrett',
      mainCourse: 'Hovedrett',
      dessert: 'Dessert',
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading-container">Laster...</div>;
  }

  return (
    <div className="feedback-management">
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="view-toggle">
        <button
          className={view === 'feedback' ? 'active' : ''}
          onClick={() => setView('feedback')}
        >
          💬 Tilbakemeldinger ({feedback.length})
        </button>
        <button
          className={view === 'meals' ? 'active' : ''}
          onClick={() => setView('meals')}
        >
          🍽️ Måltidserfaringer ({mealExperiences.length})
        </button>
      </div>

      {view === 'feedback' && (
        <div className="feedback-section">
          {feedback.length === 0 ? (
            <p className="empty-state">Ingen tilbakemeldinger ennå.</p>
          ) : (
            <div className="feedback-list">
              {feedback.map((item) => (
                <div key={item.id} className="feedback-card">
                  <div className="card-header">
                    <div>
                      <h3>{item.eventName}</h3>
                      <p className="event-info">
                        {item.organizerName} •{' '}
                        {new Date(item.eventDate).toLocaleDateString('nb-NO')}
                      </p>
                    </div>
                    <div className="ratings-compact">
                      <div>
                        <small>Opplevelse:</small>
                        {renderStars(item.overallRating)}
                      </div>
                      <div>
                        <small>Plattform:</small>
                        {renderStars(item.platformRating)}
                      </div>
                      <div>
                        <small>Arrangør:</small>
                        {renderStars(item.organizerRating)}
                      </div>
                    </div>
                  </div>

                  {item.testimonial && (
                    <div className="testimonial-content">
                      <h4>Original tilbakemelding:</h4>
                      <p className="original-text">{item.testimonial.text}</p>
                      <p className="participant-info">
                        {item.testimonial.allowPublicUse ? (
                          <>
                            ✓ Godkjent for publisering som:{' '}
                            <strong>{item.testimonial.participantName}</strong>
                          </>
                        ) : (
                          '✗ Ikke godkjent for publisering'
                        )}
                      </p>

                      {item.testimonial.allowPublicUse && (
                        <>
                          <div className="public-text-section">
                            <h4>Redigert tekst (for publisering):</h4>
                            {editingFeedback === item.id ? (
                              <div className="edit-section">
                                <textarea
                                  value={editedText}
                                  onChange={(e) => setEditedText(e.target.value)}
                                  rows={4}
                                  className="edit-textarea"
                                />
                                <div className="edit-actions">
                                  <button
                                    onClick={() =>
                                      updateFeedback(
                                        item.id,
                                        editedText,
                                        item.approvedForPublic || false
                                      )
                                    }
                                    className="btn-save"
                                  >
                                    Lagre
                                  </button>
                                  <button
                                    onClick={() => setEditingFeedback(null)}
                                    className="btn-cancel"
                                  >
                                    Avbryt
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="public-text-display">
                                <p>{item.publicText || item.testimonial.text}</p>
                                <button
                                  onClick={() => {
                                    setEditingFeedback(item.id);
                                    setEditedText(
                                      item.publicText || item.testimonial?.text || ''
                                    );
                                  }}
                                  className="btn-edit"
                                >
                                  Rediger
                                </button>
                              </div>
                            )}
                          </div>

                          <div className="approval-section">
                            <label className="approval-toggle">
                              <input
                                type="checkbox"
                                checked={item.approvedForPublic || false}
                                onChange={(e) =>
                                  updateFeedback(
                                    item.id,
                                    item.publicText || item.testimonial?.text || '',
                                    e.target.checked
                                  )
                                }
                              />
                              <span className="toggle-label">
                                {item.approvedForPublic
                                  ? '✓ Godkjent for publisering på nettside'
                                  : '✗ Ikke publisert på nettside'}
                              </span>
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <div className="card-footer">
                    <small>
                      Innsendt:{' '}
                      {new Date(item.createdAt).toLocaleString('nb-NO')}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'meals' && (
        <div className="meals-section">
          {mealExperiences.length === 0 ? (
            <p className="empty-state">Ingen måltidserfaringer ennå.</p>
          ) : (
            <div className="meals-grid">
              {mealExperiences.map((meal) => (
                <div key={meal.id} className="meal-card">
                  <div className="meal-header">
                    <span className="meal-type-badge">
                      {getMealTypeLabel(meal.mealType)}
                    </span>
                    <h3>{meal.mealName}</h3>
                  </div>

                  <div className="meal-info">
                    <p className="event-name">{meal.eventName}</p>
                    <p className="event-details">
                      {meal.eventLocation} •{' '}
                      {new Date(meal.eventDate).toLocaleDateString('nb-NO')}
                    </p>
                  </div>

                  {editingMeal === meal.id ? (
                    <div className="edit-section">
                      <input
                        type="text"
                        value={editedMealName}
                        onChange={(e) => setEditedMealName(e.target.value)}
                        placeholder="Rett navn"
                        className="edit-input"
                      />
                      <textarea
                        value={editedMealDesc}
                        onChange={(e) => setEditedMealDesc(e.target.value)}
                        placeholder="Beskrivelse"
                        rows={3}
                        className="edit-textarea"
                      />
                      <div className="edit-actions">
                        <button
                          onClick={() =>
                            updateMealExperience(
                              meal.id,
                              editedMealName,
                              editedMealDesc,
                              meal.approvedForPublic || false
                            )
                          }
                          className="btn-save"
                        >
                          Lagre
                        </button>
                        <button
                          onClick={() => setEditingMeal(null)}
                          className="btn-cancel"
                        >
                          Avbryt
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="meal-content">
                      <p className="meal-description">{meal.description}</p>
                      <p className="meal-cost">
                        Estimert kostnad: <strong>{meal.estimatedCost} kr</strong>
                      </p>
                      <button
                        onClick={() => {
                          setEditingMeal(meal.id);
                          setEditedMealName(meal.mealName);
                          setEditedMealDesc(meal.description);
                        }}
                        className="btn-edit"
                      >
                        Rediger
                      </button>
                    </div>
                  )}

                  <div className="approval-section">
                    <label className="approval-toggle">
                      <input
                        type="checkbox"
                        checked={meal.approvedForPublic || false}
                        onChange={(e) =>
                          updateMealExperience(
                            meal.id,
                            meal.mealName,
                            meal.description,
                            e.target.checked
                          )
                        }
                      />
                      <span className="toggle-label">
                        {meal.approvedForPublic
                          ? '✓ Godkjent for publisering'
                          : '✗ Ikke publisert'}
                      </span>
                    </label>
                  </div>

                  <div className="card-footer">
                    <small>
                      Innsendt:{' '}
                      {new Date(meal.submittedAt).toLocaleString('nb-NO')}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
